import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/db';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';


// User interface
interface User {
  id: string;
  email?: string;
  phone?: string;
  contact_name: string;
  role: 'user' | 'provider' | 'seller';
  is_provider: boolean;
  is_seller: boolean;
  providerProfile?: any;
  sellerProfile?: any;
  image_url?: string;
  bio?: string;
  referral?: string;
}

// AuthContext interface
interface AuthContextType {
  user: User | null;
  session: any; // Replace 'any' with Session type if available
  login: (identifier: string, password: string) => Promise<void>;
  signup: (data: {
    email?: string;
    phone?: string;
    password: string;
    contactName: string;
    referralCode?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  toggleProviderMode: () => Promise<void>;
  toggleSellerMode: () => Promise<void>;
  updateUserData: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isLoading: boolean;
  phoneLogin: (phone: string) => Promise<void>;
  verifyOtp: (phone: string, token: string, contactName?: string) => Promise<void>;
  telegramSendOtp: (phone: string) => Promise<void>;
  telegramVerifyOtp: (phone: string, code: string, contactName?: string) => Promise<void>;
  setIsLoading: (loading: boolean) => void;
  setUserRole: (role: string) => void; // Adjust type if needed
  pendingReferralCode: string | null;
  setPendingReferralCode: (code: string | null) => void;
}

// Default AuthContext value
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  login: async () => {},
  signup: async () => {},
  logout: async () => {},
  toggleProviderMode: async () => {},
  toggleSellerMode: async () => {},
  updateUserData: async () => {},
  resetPassword: async () => {},
  isLoading: true,
  phoneLogin: async () => {},
  verifyOtp: async () => {},
  telegramSendOtp: async () => {},
  telegramVerifyOtp: async () => {},
  setIsLoading: () => {},
  setUserRole: () => {},
  pendingReferralCode: null,
  setPendingReferralCode: () => {},
});

// Helper function to get readable auth errors
const getReadableAuthError = (error: any, t: any): string => {
  console.error('Auth error details:', error);
  
  if (error?.message) {
    if (error.message.includes('Email not confirmed')) {
      return t('emailNotConfirmed') || 'Please check your email for a confirmation link';
    }
    if (error.message.includes('Invalid login credentials')) {
      return t('invalidCredentials') || 'Invalid email or password';
    }
    if (error.message.includes('User already registered')) {
      return t('userAlreadyExists') || 'An account with this email already exists';
    }
    if (error.message.includes('Password should be at least')) {
      return t('passwordTooShort') || 'Password should be at least 6 characters';
    }
    // Add Supabase-specific phone auth errors
    if (error.message.includes('Phone number format is invalid')) {
      return t('invalidPhoneFormat') || 'Please enter a valid phone number with country code (e.g. +1234567890)';
    }
    if (error.message.includes('Invalid OTP')) {
      return t('invalidOTP') || 'Invalid verification code. Please try again.';
    }
  }
  
  return error?.message || t('errorGeneric') || 'An error occurred';
};

// AuthProvider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentRole, setCurrentRole] = useState<string | undefined>(undefined);
  const [pendingReferralCode, setPendingReferralCode] = useState<string | null>(null);
  const { t } = useLanguage();

  // Update user data from Supabase
  const updateUserData = async () => {
    try {
      setIsLoading(true);
      
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('No active session');
        setUser(null);
        return;
      }

      // Get user from database using id directly
      let { data: foundUser, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();
        
      if (userError) throw userError;
      
      // Fallback: create public.users record if missing
      if (!foundUser) {
        console.warn('No record found in users table, creating...');
        const { error: insertErr } = await supabase
          .from('users')
          .insert({
            id: session.user.id,
            email: session.user.email || null,
            phone: session.user.phone || null,
            created_at: new Date().toISOString()
          });
        if (insertErr) {
          console.error('Error creating users record:', insertErr);
          return;
        }
        // Re-fetch the user
        const { data: refetched } = await supabase
          .from('users')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();
        foundUser = refetched;
        if (!foundUser) return;
      }
      
      // Get profile data — create if missing
      let { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select(`*, provider_profiles (*), seller_profiles (*)`)
        .eq('user_id', session.user.id)
        .maybeSingle();
      
      if (profileError) throw profileError;
      
      if (!profile) {
        console.warn('No profile found, creating...');
        const { error: profileInsertErr } = await supabase
          .from('profiles')
          .insert({
            user_id: session.user.id,
            contact_name: session.user.email || session.user.phone || 'User',
            email: session.user.email || null,
            phone: session.user.phone || null,
            role: 'user',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        if (profileInsertErr) {
          console.error('Error creating profile:', profileInsertErr);
          return;
        }
        // Re-fetch with joins
        const { data: refetchedProfile } = await supabase
          .from('profiles')
          .select(`*, provider_profiles (*), seller_profiles (*)`)
          .eq('user_id', session.user.id)
          .maybeSingle();
        profile = refetchedProfile;
        if (!profile) return;
      }
      
      const userData: User = {
        id: foundUser.id,
        email: profile.email || session.user.email,
        phone: profile.phone || session.user.phone,
        contact_name: profile.contact_name,
        role: profile.role,
        is_provider: profile.role === 'provider',
        is_seller: profile.role === 'seller',
        providerProfile: profile.provider_profiles,
        sellerProfile: profile.seller_profiles,
        image_url: profile.image_url,
        bio: profile.bio,
        referral: profile.referral,
      };
      
      setUser(userData);
    } catch (error: any) {
      console.error('[updateUserData] Error updating user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Switch user role
  const switchRole = async (newRole: 'user' | 'provider' | 'seller') => {
    try {
      setIsLoading(true);
      
      // Get current session to get the auth_id
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ role: newRole, updated_at: new Date().toISOString() })
        .eq('user_id', session.user.id);
      
      if (updateError) throw updateError;
      
      // Update local user data
      await updateUserData();
    } catch (error: any) {
      console.error('Error switching role:', error);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle provider mode
  const toggleProviderMode = async () => {
    try {
      await switchRole('provider');
      toast.success(t('providerModeEnabled'));
    } catch (error: any) {
      console.error('Toggle provider error:', error);
      toast.error(t('providerModeError'));
    }
  };

  // Toggle seller mode
  const toggleSellerMode = async () => {
    try {
      await switchRole('seller');
      toast.success(t('sellerModeEnabled'));
    } catch (error: any) {
      console.error('Toggle seller error:', error);
      toast.error(t('sellerModeError'));
    }
  };

  // Login function
  const login = async (identifier: string, password: string) => {
    try {
      setIsLoading(true);
      
      // Check if identifier is email or phone
      const isEmail = identifier.includes('@');
      let authResponse;
      
      if (isEmail) {
        // Email login
        authResponse = await supabase.auth.signInWithPassword({
          email: identifier,
          password: password
        });
      } else {
        // Identifier might be a username or invalid format
        throw new Error('Please use a valid email address');
      }
      
      if (authResponse.error) throw authResponse.error;
      
      // Successfully logged in, update user data
      await updateUserData();
      toast.success(t('loginSuccess'));
    } catch (error: any) {
      const errorMessage = getReadableAuthError(error, t);
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Update phoneLogin function with better formatting and logging
  const phoneLogin = async (phone: string): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Ensure phone number is properly formatted (only one + at the start)
      let formattedPhone = phone.trim();
      if (formattedPhone.startsWith('++')) {
        formattedPhone = formattedPhone.substring(1); // Remove extra +
      } else if (!formattedPhone.startsWith('+')) {
        formattedPhone = `+${formattedPhone}`;
      }
      
      console.log('Sending OTP to formatted phone:', formattedPhone);
      
      // First check if user exists
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('phone')
        .eq('phone', formattedPhone)
        .maybeSingle();

      // Send OTP via Supabase with WhatsApp channel
      const { data, error } = await supabase.auth.signInWithOtp({
        phone: formattedPhone,
        options: { 
          channel: 'whatsapp' as any,
          // Only include metadata for new users
          ...((!existingUser && {
            data: {
              phone: formattedPhone
            }
          }))
        }
      });
      
      if (error) {
        console.error('OTP send error:', error);
        throw error;
      }
      
      console.log('OTP sent successfully, response:', data);
      toast.success(t('otpSent'));
    } catch (error: any) {
      const errorMessage = getReadableAuthError(error, t);
      console.error('Phone login error details:', error);
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Modified verifyOtp function to better handle profile creation
  const verifyOtp = async (phone: string, token: string, contactName?: string): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Ensure phone number is properly formatted
      let formattedPhone = phone.trim();
      if (formattedPhone.startsWith('++')) {
        formattedPhone = formattedPhone.substring(1); // Remove extra +
      } else if (!formattedPhone.startsWith('+')) {
        formattedPhone = `+${formattedPhone}`;
      }
      
      console.log('Verifying OTP for phone:', formattedPhone);
      
      // Verify the OTP
      const { data, error } = await supabase.auth.verifyOtp({
        phone: formattedPhone,
        token: token.trim(),
        type: 'sms'
      });
      
      if (error) {
        console.error('OTP verification error:', error);
        throw error;
      }
      
      console.log('OTP verified successfully, response:', data);
      
      // Check if trigger created the profile; if not, create as fallback
      // (covers edge cases like trigger failure or returning users)
      if (data.user) {
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('user_id', data.user.id)
          .maybeSingle();
        
        if (!existingProfile) {
          console.log('Trigger did not create profile, inserting fallback for:', data.user.id);
          
          // Ensure public.users record exists
          await supabase
            .from('users')
            .upsert({
              id: data.user.id,
              email: data.user.email || null,
              phone: formattedPhone,
              created_at: new Date().toISOString()
            }, { onConflict: 'id' });
          
          // Create profile
          await supabase
            .from('profiles')
            .upsert({
              user_id: data.user.id,
              contact_name: contactName || formattedPhone,
              email: data.user.email || null,
              phone: formattedPhone,
              role: 'user',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }, { onConflict: 'user_id' });
        } else if (contactName) {
          // Update existing profile with contact name if provided
          console.log('Updating profile with contact name:', contactName);
          await supabase
            .from('profiles')
            .update({
              contact_name: contactName,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', data.user.id);
        }
      }
      
      // Now update user data
      await updateUserData();
      toast.success(t('loginSuccess'));
    } catch (error: any) {
      const errorMessage = getReadableAuthError(error, t);
      console.error('Verify OTP error details:', error);
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Send OTP via Telegram Gateway
  const telegramSendOtp = async (phone: string): Promise<void> => {
    try {
      setIsLoading(true);

      // Ensure phone number is properly formatted
      let formattedPhone = phone.trim();
      if (formattedPhone.startsWith('++')) {
        formattedPhone = formattedPhone.substring(1);
      } else if (!formattedPhone.startsWith('+')) {
        formattedPhone = `+${formattedPhone}`;
      }

      console.log('Sending Telegram OTP to:', formattedPhone);

      const { data, error } = await supabase.functions.invoke('telegram-send-otp', {
        body: { phone: formattedPhone },
      });

      if (error) {
        console.error('Telegram send OTP error:', error);
        throw new Error(error.message || 'Failed to send Telegram verification');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      console.log('Telegram OTP sent successfully');
      toast.success(t('telegramOtpSent') || 'Verification code sent via Telegram');
    } catch (error: any) {
      const errorMessage = getReadableAuthError(error, t);
      console.error('Telegram send OTP error details:', error);
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP via Telegram Gateway and create session
  const telegramVerifyOtp = async (phone: string, code: string, contactName?: string): Promise<void> => {
    try {
      setIsLoading(true);

      // Ensure phone number is properly formatted
      let formattedPhone = phone.trim();
      if (formattedPhone.startsWith('++')) {
        formattedPhone = formattedPhone.substring(1);
      } else if (!formattedPhone.startsWith('+')) {
        formattedPhone = `+${formattedPhone}`;
      }

      console.log('Verifying Telegram OTP for:', formattedPhone);

      const { data, error } = await supabase.functions.invoke('telegram-verify-otp', {
        body: { phone: formattedPhone, code: code.trim(), contactName },
      });

      if (error) {
        console.error('Telegram verify OTP error:', error);
        throw new Error(error.message || 'Failed to verify Telegram code');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (!data?.session?.access_token || !data?.session?.refresh_token) {
        throw new Error('Invalid session returned from verification');
      }

      // Set the session in Supabase client
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });

      if (sessionError) {
        console.error('Error setting session:', sessionError);
        throw sessionError;
      }

      // Update user data
      await updateUserData();
      toast.success(t('loginSuccess'));
    } catch (error: any) {
      const errorMessage = getReadableAuthError(error, t);
      console.error('Telegram verify OTP error details:', error);
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Signup function with duplicate check
  const signup = async (data: {
    email?: string;
    phone?: string;
    password: string;
    contactName: string;
    referralCode?: string;
  }) => {
    try {
      setIsLoading(true);
      
      if (!data.email && !data.phone) {
        throw new Error('Either email or phone is required');
      }
      
      // Create auth user first (this will trigger your database functions)
      let authResponse;
      
      if (data.email) {
        authResponse = await supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: {
            data: {
              contact_name: data.contactName
            }
          }
        });
      } else if (data.phone) {
        throw new Error('Please use the phone verification process');
      }
      
      if (authResponse?.error) throw authResponse.error;
      
      // For email signups that need verification, the trigger won't create
      // profiles until the email is confirmed. Only update if profile exists.
      if (data.email && authResponse.data.user?.email_confirmed_at === null) {
        // Profile doesn't exist yet — it will be created when user clicks email link
        toast.custom(t('checkEmailConfirmation') || 'Please check your email to confirm your account');
      } else {
        // OAuth or already-confirmed: profile should exist, update it
        const { error: profileUpdateError } = await supabase
          .from('profiles')
          .update({ 
            contact_name: data.contactName,
            referral: data.referralCode || null,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', authResponse.data.user.id);
        
        if (profileUpdateError) {
          console.warn('Profile update warning:', profileUpdateError);
        }

        await updateUserData();
        toast.success(t('signupSuccess'));
      }
    } catch (error: any) {
      const errorMessage = getReadableAuthError(error, t);
      toast.error(errorMessage);
      console.error('Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced logout function with fixes for race conditions
  const logout = async () => {
    try {
      setIsLoading(true);
      
      console.log('Starting logout process...');
      
      // Sign out from Supabase first (scope 'global' ensures server session is also cleared for OAuth)
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      
      if (error) throw error;
      console.log('Supabase signOut completed');
      
      // Success message
      toast.success(t('logoutSuccess'));
    } catch (error: any) {
      console.error('Logout error:', error);
      toast.error(error.message || 'Logout failed');
    } finally {
      // Always clear state, even if signOut errored
      setUser(null);
      setSession(null);
      
      // Clear any remaining Supabase localStorage keys
      Object.keys(localStorage)
        .filter(key => key.startsWith('sb-'))
        .forEach(key => localStorage.removeItem(key));
      
      setIsLoading(false);
      console.log('User state cleared');
    }
  };

  // Reset password function - Fix implementation issues
  const resetPassword = async (email: string): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Fixed resetPasswordForEmail call with proper options
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password` // Make sure this route exists
      });
      
      if (error) throw error;
      
      // Show success message and guidance
      toast.success(t('passwordResetEmailSent'));
      toast.custom(t('checkEmailForResetLink') || 'Please check your email inbox for the password reset link');
    } catch (error: any) {
      const errorMessage = getReadableAuthError(error, t);
      toast.error(errorMessage);
      console.error('Password reset error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Listen to auth changes
  useEffect(() => {
    // Track whether initSession already loaded user data.
    // This prevents the duplicate updateUserData call from onAuthStateChange
    // which creates a new user object reference, cascading re-renders and
    // tearing down/rebuilding NotificationService.
    let userLoaded = false;

    // Get initial session
    const initSession = async () => {
      setIsLoading(true);
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        await updateUserData();
        userLoaded = true;
      } else {
        setIsLoading(false);
      }
    };
    initSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // Skip if initSession already loaded user data for this session
        if (!userLoaded) {
          await updateUserData();
          userLoaded = true;
        }
      } else if (event === 'SIGNED_OUT') {
        userLoaded = false;
        setUser(null);
        setSession(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Function to set user role (if needed by context)
  const setUserRole = (role: string) => {
      setCurrentRole(role);
      // Optionally update user state if role is part of it
      // setUser(prevUser => ({ ...prevUser, role })); 
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        login,
        signup,
        logout,
        toggleProviderMode,
        toggleSellerMode,
        updateUserData,
        resetPassword,
        isLoading,
        phoneLogin,
        verifyOtp,
        telegramSendOtp,
        telegramVerifyOtp,
        setIsLoading,
        setUserRole,
        pendingReferralCode,
        setPendingReferralCode,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);